import { type FC } from 'react';
import { Flex, Badge, Button, Divider, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { RedoOutlined } from '@ant-design/icons';
import InteractionStatusSelect from 'components/molecules/select/interactionStatus';
import InteractiveTimeSelect from 'components/molecules/select/interactiveTime';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import TimezoneSelectBox from 'components/molecules/timezone-select-box';
import LanguageSelectBox from 'components/molecules/language-select-box';
import ReadingTypeSelectBox from 'components/molecules/reading-type-select-box';
import AgencyDoctorSelectBox from 'components/molecules/agency-doctor-select-box';
import ClinicDropdownSelectBox from 'components/molecules/clinic-dropdown-select-box';
import ClinicSelectBox from 'components/molecules/clinic-select-box';
import StateSelectBox from 'components/molecules/states-types-select-box';
import { interactionStatusRpmOptions } from 'constants/forms';
import { ParentClinicOption } from 'infrastructure/enums/parent-clinic';
import { ReadingStatus } from 'infrastructure/enums';
import useUser from 'utils/useUser';

import styles from './styles.module.scss';

import type { Option } from 'components/molecules/select/base';
import type { InteractionStatusEnum } from 'infrastructure/enums';
import type { TOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';

interface RpmDashboardFilterPopoverProps {
  filter: TOverviewDashboardFilter;
  updateFilter: (newFilter: Partial<TOverviewDashboardFilter>) => void;
  count: number;
  resetFilter: () => void;
  onClinicLoaded?: (clinicName: string | null) => void;
  onAgencyDoctorLoaded?: (agencyDoctorName: string | null) => void;
}

const RpmDashboardFilterPopover: FC<RpmDashboardFilterPopoverProps> = ({
  filter,
  updateFilter,
  count,
  resetFilter,
  onClinicLoaded,
  onAgencyDoctorLoaded,
}) => {
  const { t } = useTranslation();
  const { isCriticalOrAtRiskAlertingRole, isSupervisorRole } = useUser();

  const hideInteractiveTime = isCriticalOrAtRiskAlertingRole;

  return (
    <div className={styles.popover}>
      <Flex align="center" justify="space-between" className={styles.header}>
        <Flex align="center" gap={8}>
          <h3 className={styles.title}>{t('controls.filters', 'Filters')}</h3>
          {count > 0 && <Badge count={count} className={styles.badge} />}
        </Flex>
        <Button
          type="default"
          icon={<RedoOutlined />}
          onClick={resetFilter}
          size="small"
        >
          {t('controls.clearFilter', 'Clear filter')}
        </Button>
      </Flex>

      <div className={styles.section}>
        <h4 className={styles['section-title']}>
          {t('labels.priorityAndWork', 'PRIORITY & WORK')}
        </h4>
        <div className={styles['row-4']}>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.readingStatus', 'Reading Status')}
            </label>
            <Select
              placeholder={t('labels.select', 'Select')}
              value={filter.status}
              onChange={(v) => updateFilter({ status: v, page: 1 })}
              allowClear
              className={styles['full-width']}
              options={[
                {
                  label: t('labels.critical', 'Critical'),
                  value: ReadingStatus.Critical,
                },
                {
                  label: t('labels.at-risk', 'At Risk'),
                  value: ReadingStatus.Risk,
                },
                {
                  label: t('labels.normal', 'Normal'),
                  value: ReadingStatus.Normal,
                },
              ]}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.activitiesType', 'Activity Type')}
            </label>
            <BooleanSelectBox
              trueLabel={t('labels.actioned', 'Actioned')}
              falseLabel={t('labels.unaction', 'Unaction')}
              value={filter.reviewed}
              onChange={(v) => updateFilter({ reviewed: v, page: 1 })}
              allowClear
            />
          </div>
          <InteractionStatusSelect<InteractionStatusEnum>
            value={(
              interactionStatusRpmOptions as Option<InteractionStatusEnum>[]
            ).find((v) => v.value === filter.interactionStatus)}
            onChange={(v) =>
              updateFilter({ interactionStatus: v?.value, page: 1 })
            }
            allowClear
          />
          {!hideInteractiveTime && (
            <div className={styles.field}>
              <label className={styles.label}>
                {t('labels.interactiveTime', 'Interactive Time')}
              </label>
              <InteractiveTimeSelect
                value={filter.interactiveTimeRange}
                onChange={(v) => {
                  updateFilter({
                    interactiveTimeRange: v,
                    page: 1,
                  });
                }}
                allowClear
              />
            </div>
          )}
        </div>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.section}>
        <h4 className={styles['section-title']}>
          {t('labels.patientClinicalContext', 'PATIENT & CLINICAL CONTEXT')}
        </h4>
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.patientTimezone', 'Patient Timezone')}
            </label>
            <TimezoneSelectBox
              value={filter.timezone}
              onChange={(e) => updateFilter({ timezone: e, page: 1 })}
              allowClear
              allowSearch
              mainlandTimezonesOnly
              withAll
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.language', 'Patient Language')}
            </label>
            <LanguageSelectBox
              value={filter.language}
              withAll
              englishSpanishOnly
              onChange={(e) => updateFilter({ language: e, page: 1 })}
              allowClear
              allowSearch
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.readingType', 'Reading Type')}
            </label>
            <ReadingTypeSelectBox
              value={filter.readingType}
              onChange={(v) => updateFilter({ readingType: v, page: 1 })}
              allowClear
              withAll
            />
          </div>
          {isSupervisorRole && (
            <div className={styles.field}>
              <label className={styles.label}>
                {t('labels.reviewedReadingBy', 'Reviewed Reading By')}
              </label>
              <AgencyDoctorSelectBox
                value={filter.actionedByAgencyDoctorGuid}
                onChange={(guid, option) => {
                  updateFilter({ actionedByAgencyDoctorGuid: guid, page: 1 });
                  if (onAgencyDoctorLoaded) {
                    onAgencyDoctorLoaded(
                      option?.label ? String(option.label) : null,
                    );
                  }
                }}
                onLoaded={(items, selected) => {
                  if (onAgencyDoctorLoaded) {
                    onAgencyDoctorLoaded(
                      selected?.label ? String(selected.label) : null,
                    );
                  }
                }}
                allowClear
              />
            </div>
          )}
        </div>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.section}>
        <h4 className={styles['section-title']}>
          {t('labels.organizationLocation', 'ORGANIZATION & LOCATION')}
        </h4>
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.parentClinic', 'Parent Clinic')}
            </label>
            <ClinicDropdownSelectBox
              value={filter.parentClinicOption}
              onChange={(v) =>
                updateFilter({
                  parentClinicOption: v,
                  clinicGuid: undefined,
                  page: 1,
                })
              }
              allowClear
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.clinic', 'Clinic')}
            </label>
            <ClinicSelectBox
              placeholder={t('labels.select', 'Select')}
              value={filter.clinicGuid}
              onSelectClinic={(guid, name) => {
                updateFilter({ clinicGuid: guid ?? undefined, page: 1 });
                if (onClinicLoaded) {
                  onClinicLoaded(name);
                }
              }}
              allowClear
              parentClinicsOnly={
                filter.parentClinicOption === ParentClinicOption.ParentClinics
              }
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('labels.clinicState', 'Clinic State')}
            </label>
            <StateSelectBox
              allowClear
              withAll
              value={filter.clinicState}
              onChange={(v) => updateFilter({ clinicState: v, page: 1 })}
              allowSearch
              filterOption={(inputValue, option) => {
                const recordName = option?.record?.name;
                if (!recordName || !inputValue) return false;
                return recordName
                  .toLowerCase()
                  .startsWith(inputValue.toLowerCase());
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RpmDashboardFilterPopover;
